(() => {
  const STORAGE_KEY = 'discountKitActiveCodes';
  const UTM_STORAGE_KEY = 'readersCampaignUtmCampaigns';
  const SESSION_EVENT_NAME = 'readers:campaign-session-change';
  const DISCOUNT_PATH_PATTERN = /\/discount\/([^/?#]+)/i;

  /**
   * Normalizes a campaign token for case-insensitive comparisons.
   * @param {string} value Campaign token to normalize.
   * @returns {string}
   */
  function normalizeToken(value) {
    return String(value || '').trim().toLowerCase();
  }

  /**
   * Reads a normalized string array from session storage.
   * @param {string} key Session storage key.
   * @returns {string[]}
   */
  function getStoredTokens(key) {
    try {
      const storedValue = window.sessionStorage.getItem(key);
      const parsedCodes = storedValue ? JSON.parse(storedValue) : [];

      return Array.isArray(parsedCodes)
        ? parsedCodes.map(normalizeToken).filter(Boolean)
        : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Persists normalized campaign tokens for the current browser session.
   * @param {string} key Session storage key.
   * @param {string[]} values Values to store.
   * @returns {string[]}
   */
  function setStoredTokens(key, values) {
    const normalizedValues = Array.from(new Set((values || []).map(normalizeToken).filter(Boolean)));

    try {
      if (normalizedValues.length > 0) {
        window.sessionStorage.setItem(key, JSON.stringify(normalizedValues));
      } else {
        window.sessionStorage.removeItem(key);
      }
    } catch (error) {
      return normalizedValues;
    }

    return normalizedValues;
  }

  /**
   * Extracts discount codes from a URL path or query string.
   * @param {string} urlValue URL to inspect.
   * @returns {string[]}
   */
  function getDiscountCodesFromUrl(urlValue) {
    const codes = [];

    try {
      const url = new URL(urlValue, window.location.origin);
      const discountPathMatch = url.pathname.match(DISCOUNT_PATH_PATTERN);

      if (discountPathMatch?.[1]) {
        codes.push(decodeURIComponent(discountPathMatch[1]));
      }

      ['discount', 'discount_code', 'promo_code', 'code'].forEach((paramName) => {
        const paramValue = url.searchParams.get(paramName);

        if (paramValue) {
          codes.push(paramValue);
        }
      });
    } catch (error) {
      return codes;
    }

    return codes.map(normalizeToken).filter(Boolean);
  }

  /**
   * Extracts UTM campaigns from a URL.
   * @param {string} urlValue URL to inspect.
   * @returns {string[]}
   */
  function getUtmCampaignsFromUrl(urlValue) {
    const campaigns = [];

    try {
      const url = new URL(urlValue, window.location.origin);
      const campaignValue = url.searchParams.get('utm_campaign');

      if (campaignValue) {
        campaigns.push(campaignValue);
      }
    } catch (error) {
      return campaigns;
    }

    return campaigns.map(normalizeToken).filter(Boolean);
  }

  /**
   * Reads an existing campaign cookie value when legacy campaign links set one.
   * @param {string} name Cookie name.
   * @returns {string}
   */
  function getCookie(name) {
    try {
      const needle = `${encodeURIComponent(name)}=`;
      const parts = document.cookie.split(';');
      const cookie = parts.map((part) => part.trim()).find((part) => part.indexOf(needle) === 0);

      return cookie ? decodeURIComponent(cookie.substring(needle.length)) : '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Creates the public campaign session API consumed by storefront components.
   * @param {string[]} codes Active discount codes.
   * @param {string[]} campaigns Active UTM campaigns.
   * @returns {Object}
   */
  function createCampaignSession(codes, campaigns) {
    const discountCodes = Array.from(new Set((codes || []).map(normalizeToken).filter(Boolean)));
    const utmCampaigns = Array.from(new Set((campaigns || []).map(normalizeToken).filter(Boolean)));

    return {
      discountCodes,
      utmCampaigns,
      primaryUtmCampaign: utmCampaigns[0] || '',
      normalize: normalizeToken,
      hasDiscountCode(code) {
        return discountCodes.includes(normalizeToken(code));
      },
      hasUtmCampaign(campaign) {
        const normalizedCampaign = normalizeToken(campaign);
        return normalizedCampaign ? utmCampaigns.includes(normalizedCampaign) : utmCampaigns.length > 0;
      },
    };
  }

  /**
   * Publishes the campaign session to the window and notifies listeners.
   * @param {string[]} codes Active discount codes.
   * @param {string[]} campaigns Active UTM campaigns.
   * @returns {Object}
   */
  function publishCampaignSession(codes, campaigns) {
    window.readersCampaignSession = createCampaignSession(codes, campaigns);
    window.readersCampaignSession.refresh = refreshCampaignSession;
    document.dispatchEvent(new CustomEvent(SESSION_EVENT_NAME, { detail: window.readersCampaignSession }));

    return window.readersCampaignSession;
  }

  /**
   * Shows session-only Discount Kit markup when its code is active.
   * @param {string[]} codes Active discount codes.
   */
  function applyDiscountKitSessionDisplays(codes) {
    const activeCodes = new Set((codes || []).map(normalizeToken).filter(Boolean));

    document.querySelectorAll('[data-discount-kit-session-only][data-discount-code-lower]').forEach((element) => {
      const code = normalizeToken(element.getAttribute('data-discount-code-lower'));
      const isActive = activeCodes.has(code);

      element.hidden = !isActive;
      element.classList.toggle('tw-hidden', !isActive);
    });

    document.querySelectorAll('.badge__wrapper').forEach((wrapper) => {
      const hasActiveSessionBadge = Array.from(
        wrapper.querySelectorAll('.discount-kit-badge[data-discount-kit-session-only][data-discount-code-lower]')
      ).some((badge) => activeCodes.has(normalizeToken(badge.getAttribute('data-discount-code-lower'))));

      wrapper.querySelectorAll('.discount-kit-badge:not([data-discount-kit-session-only])').forEach((badge) => {
        badge.hidden = hasActiveSessionBadge;
        badge.classList.toggle('tw-hidden', hasActiveSessionBadge);
      });
    });

    document.querySelectorAll('[data-discount-kit-code-price][data-discount-code-lower]').forEach((container) => {
      const code = normalizeToken(container.getAttribute('data-discount-code-lower'));
      const isActive = activeCodes.has(code);
      const regularPrice = container.querySelector('[data-discount-kit-regular-price]');

      if (regularPrice) {
        regularPrice.hidden = isActive;
        regularPrice.classList.toggle('tw-hidden', isActive);
      }
    });

    document.querySelectorAll('[data-discount-kit-default-price][data-discount-code-lower]').forEach((container) => {
      const code = normalizeToken(container.getAttribute('data-discount-code-lower'));
      const isActive = activeCodes.has(code);

      container.hidden = isActive;
      container.classList.toggle('tw-hidden', isActive);
    });
  }

  /**
   * Applies active codes to Shopify's cart session when the cart API supports it.
   * @param {string[]} codes Active discount codes.
   */
  function applyCodesToCart(codes) {
    const normalizedCodes = (codes || []).map(normalizeToken).filter(Boolean);

    if (normalizedCodes.length === 0 || !window.routes?.cart_update_url) {
      return;
    }

    window.fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: normalizedCodes.join(',') }),
    }).catch(() => {});
  }

  /**
   * Reads Shopify cart discount codes that were stored by /discount links.
   * @returns {Promise<string[]>}
   */
  function getDiscountCodesFromCart() {
    if (!window.routes?.cart_url) {
      return Promise.resolve([]);
    }

    return window.fetch(`${window.routes.cart_url}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart || !Array.isArray(cart.discount_codes)) {
          return [];
        }

        return cart.discount_codes.map((discount) => discount?.code).filter(Boolean);
      })
      .catch(() => []);
  }

  /**
   * Refreshes active campaign state from URL, referrer, storage and cart data.
   * @returns {Promise<Object>}
   */
  function refreshCampaignSession() {
    const initialCodes = [
      ...getStoredTokens(STORAGE_KEY),
      ...getDiscountCodesFromUrl(window.location.href),
      ...getDiscountCodesFromUrl(document.referrer),
    ];
    const initialCampaigns = [
      ...getStoredTokens(UTM_STORAGE_KEY),
      ...getUtmCampaignsFromUrl(window.location.href),
      ...getUtmCampaignsFromUrl(document.referrer),
      getCookie('utm'),
      getCookie('utm_campaign_cached'),
    ];

    let activeCodes = setStoredTokens(STORAGE_KEY, initialCodes);
    let activeCampaigns = setStoredTokens(UTM_STORAGE_KEY, initialCampaigns);
    publishCampaignSession(activeCodes, activeCampaigns);
    applyDiscountKitSessionDisplays(activeCodes);
    applyCodesToCart(activeCodes);

    return getDiscountCodesFromCart().then((cartCodes) => {
      activeCodes = setStoredTokens(STORAGE_KEY, activeCodes.concat(cartCodes));
      activeCampaigns = setStoredTokens(UTM_STORAGE_KEY, activeCampaigns);
      const session = publishCampaignSession(activeCodes, activeCampaigns);
      applyDiscountKitSessionDisplays(activeCodes);

      return session;
    });
  }

  window.readersCampaignSession = createCampaignSession(
    [
      ...getStoredTokens(STORAGE_KEY),
      ...getDiscountCodesFromUrl(window.location.href),
      ...getDiscountCodesFromUrl(document.referrer),
    ],
    [
      ...getStoredTokens(UTM_STORAGE_KEY),
      ...getUtmCampaignsFromUrl(window.location.href),
      ...getUtmCampaignsFromUrl(document.referrer),
      getCookie('utm'),
      getCookie('utm_campaign_cached'),
    ]
  );
  window.readersCampaignSession.refresh = refreshCampaignSession;
  refreshCampaignSession();

  document.addEventListener('DOMContentLoaded', () => {
    applyDiscountKitSessionDisplays(getStoredTokens(STORAGE_KEY));

    const observer = new MutationObserver(() => {
      applyDiscountKitSessionDisplays(getStoredTokens(STORAGE_KEY));
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
